/**
 * KeywordResearchHistoryPanel
 *
 * Mounts on the "History" sub-tab of /ads-tools. Left
 * column lists past research runs, right column renders the selected
 * run's clusters with used-vs-not-used pills.
 *
 * Backed by /google-ads/keyword-research/history{,/{run_id}} (P1.X
 * backend, commit 75f16cd7). Plan: ai-docs/google-ads/p1-history-tab-plan.md.
 *
 * "Re-run with this context" button calls `onRerun` (passed by parent
 * page) with the historical run's company_information + pages, then
 * the parent switches tabs to "Run new research" and AdCampaignKeywords
 * Panel's defaults-sync effect populates the form.
 */
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  useActiveResearchRun,
  useResearchHistory,
  useResearchRun,
} from '@/hooks/useKeywordResearchHistory';
import type {
  KeywordResearchHistoryItem,
  KeywordResearchHistoryClusterDetail,
} from '@/services/keywordResearchHistoryService';
import {
  keywordResearchHistoryQueryKeys,
  keywordResearchHistoryService,
} from '@/services/keywordResearchHistoryService';
import {
  buildKeywordResearchRunComparison,
  normalizeKeywordResearchText,
  type KeywordResearchClusterComparison,
} from '@/components/keyword-research/keywordResearchHistoryDiff';
import {
  ArrowUpRight,
  CalendarClock,
  CheckCircle2,
  ExternalLink,
  History,
  Layers,
  PlusCircle,
  RefreshCw,
  XCircle,
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';

interface KeywordResearchHistoryPanelProps {
  tenantId: string;
  /** When the user clicks "Re-run with this context" on a detail row,
   *  the parent should switch tabs back to "Run new research" and
   *  pre-fill the form with this context. */
  onRerun: (ctx: { company_information: string; pages: string[] }) => void;
  onNewResearch: () => void;
  /** Optional initial run to select on mount — used by URL state
   *  (?research_tab=history&run=<uuid>) to make detail links shareable. */
  initialSelectedRunId?: string | null;
  /** Called when the selected run changes so the parent can sync the
   *  URL query string. */
  onSelectedRunChange?: (runId: string | null) => void;
}

function formatRelative(iso: string): string {
  try {
    const then = new Date(iso).getTime();
    const now = Date.now();
    const diffMs = now - then;
    const minutes = Math.round(diffMs / 60_000);
    if (minutes < 1) return 'just now';
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.round(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.round(hours / 24);
    if (days < 30) return `${days}d ago`;
    return new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return iso;
  }
}

function formatAbsolute(iso: string): string {
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZoneName: 'short',
    });
  } catch {
    return iso;
  }
}

function hasSavedSelectionActivity(run: {
  created_at: string;
  activity_at?: string | null;
  selection_saved_at?: string | null;
}): boolean {
  if (!run.selection_saved_at || !run.activity_at) return false;
  return new Date(run.activity_at).getTime() > new Date(run.created_at).getTime();
}

function SourceBadge({ source }: { source: KeywordResearchHistoryItem['source'] }) {
  if (source === 'campaign_run') {
    return <Badge className="bg-coral-light text-coral-dark text-xs">Campaign</Badge>;
  }
  return <Badge variant="secondary" className="text-xs">Standalone</Badge>;
}

function UsedRatio({ used, total }: { used: number; total: number }) {
  if (total === 0) return <span className="text-xs text-muted-foreground">no clusters</span>;
  const pct = Math.round((used / total) * 100);
  return (
    <div className="flex items-center gap-2">
      <div className="h-1.5 flex-1 rounded-full bg-warm-bg overflow-hidden min-w-[60px]">
        <div
          className="h-full bg-teal transition-all"
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className="text-xs tabular-nums text-muted-foreground whitespace-nowrap">
        {used}/{total} used
      </span>
    </div>
  );
}

/** True when the failure is "this run is not there", not "the request broke".
 *
 * Axios puts the status on error.response.status; some callers surface only a
 * message. Both are checked because getting this wrong in either direction is
 * worse than the original bug: a real outage rendered as "no longer exists"
 * would hide it. */
function isMissingRunError(error: unknown): boolean {
  const status = (error as { response?: { status?: number } })?.response?.status;
  if (status === 404) return true;
  const message = String((error as Error)?.message ?? '');
  return /\b404\b/.test(message);
}

export function KeywordResearchHistoryPanel({
  tenantId,
  onRerun,
  onNewResearch,
  initialSelectedRunId = null,
  onSelectedRunChange,
}: KeywordResearchHistoryPanelProps) {
  const PAGE_SIZE = 20;
  const [offset, setOffset] = useState(0);
  const [selectedRunId, setSelectedRunId] = useState<string | null>(initialSelectedRunId);
  const onSelectedRunChangeRef = useRef(onSelectedRunChange);
  const lastNotifiedRunIdRef = useRef<string | null | undefined>(undefined);
  const lastInitialRunIdRef = useRef<string | null>(initialSelectedRunId);
  const queryClient = useQueryClient();

  useEffect(() => {
    onSelectedRunChangeRef.current = onSelectedRunChange;
  }, [onSelectedRunChange]);

  useEffect(() => {
    if (initialSelectedRunId === lastInitialRunIdRef.current) return;
    lastInitialRunIdRef.current = initialSelectedRunId;
    if (initialSelectedRunId) {
      setSelectedRunId(initialSelectedRunId);
    }
  }, [initialSelectedRunId]);

  const listQuery = useResearchHistory({
    tenant_id: tenantId,
    limit: PAGE_SIZE,
    offset,
  });
  const activeQuery = useActiveResearchRun(tenantId);
  const activeRunId = activeQuery.data?.active_run_id ?? null;
  const setActiveMutation = useMutation({
    mutationFn: (runId: string) =>
      keywordResearchHistoryService.setActive({
        tenant_id: tenantId,
        run_id: runId,
      }),
    onSuccess: (result) => {
      toast.success('Active keyword cluster set updated');
      void queryClient.invalidateQueries({
        queryKey: keywordResearchHistoryQueryKeys.active(tenantId),
      });
      void queryClient.invalidateQueries({
        queryKey: keywordResearchHistoryQueryKeys.lists(),
      });
      if (result.active_run_id) {
        void queryClient.invalidateQueries({
          queryKey: keywordResearchHistoryQueryKeys.detail(result.active_run_id),
        });
      }
    },
    onError: (err) => {
      toast.error(
        `Could not set active cluster set: ${String((err as Error)?.message ?? err)}`,
      );
    },
  });

  // Auto-select the first run when the list first loads and nothing is
  // selected yet. Keeps the right pane from sitting empty on first
  // visit. Doesn't override an existing selection.
  useEffect(() => {
    if (selectedRunId) return;
    const first = listQuery.data?.runs?.[0];
    if (first) {
      setSelectedRunId(first.id);
    }
  }, [listQuery.data, selectedRunId]);

  // Sync URL when the user changes selection
  useEffect(() => {
    if (lastNotifiedRunIdRef.current === selectedRunId) return;
    lastNotifiedRunIdRef.current = selectedRunId;
    onSelectedRunChangeRef.current?.(selectedRunId);
  }, [selectedRunId]);

  const detailQuery = useResearchRun(selectedRunId);
  const comparisonRunId = useMemo(() => {
    if (!selectedRunId || !listQuery.data?.runs?.length) return null;
    const currentIndex = listQuery.data.runs.findIndex((run) => run.id === selectedRunId);
    if (currentIndex < 0) return null;
    return listQuery.data.runs[currentIndex + 1]?.id ?? null;
  }, [listQuery.data?.runs, selectedRunId]);
  const comparisonQuery = useResearchRun(comparisonRunId);

  const totalPages = useMemo(() => {
    if (!listQuery.data?.total) return 0;
    return Math.ceil(listQuery.data.total / PAGE_SIZE);
  }, [listQuery.data]);
  const currentPage = Math.floor(offset / PAGE_SIZE) + 1;

  return (
    <div className="grid gap-4 lg:grid-cols-[340px_1fr]">
      {/* ── Left: runs list ────────────────────────────────────── */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between gap-3">
            <div>
              <CardTitle className="flex items-center gap-2 text-base">
                <History className="h-4 w-4 text-teal" />
                Past runs
              </CardTitle>
              <CardDescription className="text-xs">
                Latest activity first. Click a run to see clusters + used state.
              </CardDescription>
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-8 shrink-0"
              onClick={onNewResearch}
            >
              <PlusCircle className="mr-1 h-3.5 w-3.5" />
              New research
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-2 px-3">
          {listQuery.isLoading ? (
            <div className="space-y-2">
              {[0, 1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-20 w-full" />
              ))}
            </div>
          ) : listQuery.isError ? (
            <div className="text-sm text-destructive p-3">
              Failed to load history. {String((listQuery.error as Error)?.message ?? '')}
            </div>
          ) : !listQuery.data?.runs.length ? (
            <div className="rounded-md border border-dashed border-border p-6 text-center text-xs text-muted-foreground">
              No past research runs yet. Run keyword research on the other
              tab — finished runs will land here.
            </div>
          ) : (
            <>
              {listQuery.data.runs.map((run) => (
                <HistoryListItem
                  key={run.id}
                  run={run}
                  active={run.id === selectedRunId}
                  isActiveSet={run.id === activeRunId}
                  onSelect={() => setSelectedRunId(run.id)}
                />
              ))}
              {totalPages > 1 && (
                <div className="flex items-center justify-between pt-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
                    disabled={offset === 0 || listQuery.isFetching}
                  >
                    Prev
                  </Button>
                  <span className="text-xs text-muted-foreground tabular-nums">
                    Page {currentPage} of {totalPages}
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setOffset(offset + PAGE_SIZE)}
                    disabled={
                      offset + PAGE_SIZE >= (listQuery.data?.total ?? 0)
                      || listQuery.isFetching
                    }
                  >
                    Next
                  </Button>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* ── Right: detail ──────────────────────────────────────── */}
      <div>
        {!selectedRunId ? (
          <Card>
            <CardContent className="p-8 text-center text-sm text-muted-foreground">
              Pick a run from the list to see its clusters.
            </CardContent>
          </Card>
        ) : detailQuery.isLoading ? (
          <Card>
            <CardContent className="p-6">
              <Skeleton className="h-6 w-1/3 mb-3" />
              <Skeleton className="h-4 w-2/3 mb-6" />
              <Skeleton className="h-24 w-full" />
            </CardContent>
          </Card>
        ) : detailQuery.isError ? (
          <Card>
            <CardContent className="p-6 text-sm">
              {/*
                A 404 here is NOT a failure — it means the run is gone, and the
                only thing that could have sent you to a dead id is a stale
                pointer (a Keyword Research campaign package saved in this
                browser, or an old bookmark). Showing "Request failed with
                status code 404" told the operator a request broke when in fact
                the link was wrong, which sends them looking for an outage.
                Runs also carry a 30-day TTL, so a link CAN legitimately outlive
                its run.
              */}
              {isMissingRunError(detailQuery.error) ? (
                <div className="space-y-2">
                  <p className="font-medium text-foreground">
                    This keyword research run no longer exists.
                  </p>
                  <p className="text-muted-foreground">
                    The link pointed at a run that is not stored any more — most
                    likely a saved campaign package in this browser, or a
                    bookmark older than the 30-day retention window. Nothing is
                    broken.
                  </p>
                  <p className="text-muted-foreground">
                    Pick a run from the list, or start a new keyword research
                    run.
                  </p>
                </div>
              ) : (
                <span className="text-destructive">
                  Failed to load run.{' '}
                  {String((detailQuery.error as Error)?.message ?? '')}
                </span>
              )}
            </CardContent>
          </Card>
        ) : detailQuery.data ? (
          <HistoryRunDetail
            run={detailQuery.data}
            comparisonRun={comparisonQuery.data ?? null}
            activeRunId={activeRunId}
            onSetActive={(runId) => setActiveMutation.mutate(runId)}
            settingActive={setActiveMutation.isPending}
            onRerun={onRerun}
            onNewResearch={onNewResearch}
          />
        ) : null}
      </div>
    </div>
  );
}

function HistoryListItem({
  run,
  active,
  isActiveSet,
  onSelect,
}: {
  run: KeywordResearchHistoryItem;
  active: boolean;
  isActiveSet: boolean;
  onSelect: () => void;
}) {
  const selectionUpdated = hasSavedSelectionActivity(run);
  const timestamp = selectionUpdated ? run.activity_at : run.created_at;
  return (
    <button
      type="button"
      onClick={onSelect}
      className={`w-full rounded-md border px-3 py-2.5 text-left transition-colors ${
        active
          ? 'border-coral bg-coral-light text-coral-dark'
          : 'border-border hover:bg-warm-bg'
      }`}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs text-muted-foreground tabular-nums whitespace-nowrap">
          {selectionUpdated ? 'Updated' : 'Created'} {formatRelative(timestamp)}
        </span>
        <div className="flex items-center gap-1">
          {isActiveSet && (
            <Badge className="bg-teal-light text-teal-dark text-xs">
              Active
            </Badge>
          )}
          <SourceBadge source={run.source} />
        </div>
      </div>
      <div className="mt-1 text-xs line-clamp-2 text-foreground/80">
        {run.company_information_preview || 'No company info'}
      </div>
      <div className="mt-2 flex items-center gap-2 text-xs">
        <Layers className="h-3 w-3 text-teal" />
        <span className="tabular-nums">
          {run.cluster_count} cluster{run.cluster_count === 1 ? '' : 's'}, {run.total_keywords} kw
        </span>
        {(run.selected_keyword_count ?? 0) > 0 && (
          <Badge variant="outline" className="bg-teal-light/20 text-teal-dark">
            {run.selected_keyword_count} saved
          </Badge>
        )}
      </div>
      <div className="mt-2">
        <UsedRatio used={run.used_cluster_count} total={run.cluster_count} />
      </div>
    </button>
  );
}

function HistoryRunDetail({
  run,
  comparisonRun,
  activeRunId,
  onSetActive,
  settingActive,
  onRerun,
  onNewResearch,
}: {
  run: import('@/services/keywordResearchHistoryService').KeywordResearchHistoryDetail;
  comparisonRun: import('@/services/keywordResearchHistoryService').KeywordResearchHistoryDetail | null;
  activeRunId: string | null;
  onSetActive: (runId: string) => void;
  settingActive: boolean;
  onRerun: (ctx: { company_information: string; pages: string[] }) => void;
  onNewResearch: () => void;
}) {
  const selectionUpdated = hasSavedSelectionActivity(run);
  const isActiveSet = run.id === activeRunId;
  const comparison = useMemo(
    () => buildKeywordResearchRunComparison(run, comparisonRun),
    [comparisonRun, run],
  );
  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2 text-base">
              <CalendarClock className="h-4 w-4 text-coral" />
              {selectionUpdated
                ? `Updated ${formatAbsolute(run.activity_at)}`
                : formatAbsolute(run.created_at)}
            </CardTitle>
            <div className="mt-1 flex items-center gap-2 text-xs text-muted-foreground">
              <SourceBadge source={run.source} />
              {selectionUpdated && (
                <>
                  <span className="text-muted-foreground">·</span>
                  <span>created {formatRelative(run.created_at)}</span>
                </>
              )}
              <span className="text-muted-foreground">·</span>
              <span>{run.clusters.length} cluster{run.clusters.length === 1 ? '' : 's'}</span>
              {run.expires_at && (
                <>
                  <span className="text-muted-foreground">·</span>
                  <span className="text-muted-foreground">
                    expires {formatRelative(run.expires_at)}
                  </span>
                </>
              )}
            </div>
          </div>
          <div className="flex shrink-0 flex-wrap justify-end gap-2">
            <Button
              variant={isActiveSet ? 'secondary' : 'outline'}
              size="sm"
              disabled={isActiveSet || settingActive}
              onClick={() => onSetActive(run.id)}
            >
              <CheckCircle2 className="mr-2 h-3.5 w-3.5" />
              {isActiveSet ? 'Active set' : 'Set active'}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={onNewResearch}
            >
              <PlusCircle className="mr-2 h-3.5 w-3.5" />
              New research
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() =>
                onRerun({
                  company_information: run.company_information,
                  pages: run.pages,
                })
              }
            >
              <RefreshCw className="mr-2 h-3.5 w-3.5" />
              Re-run with this context
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-5">
        {comparison ? (
          <div className="rounded border border-teal/30 bg-teal-light/20 p-3 text-xs text-foreground">
            <div className="font-medium">Compared with the previous saved run</div>
            <div className="mt-2 flex flex-wrap gap-2">
              <Badge variant="outline" className="border-teal/30 bg-card text-teal-dark">
                {comparison.new_cluster_count} new cluster
                {comparison.new_cluster_count === 1 ? '' : 's'}
              </Badge>
              <Badge variant="outline" className="border-coral/30 bg-card text-coral-dark">
                {comparison.changed_cluster_count} changed cluster
                {comparison.changed_cluster_count === 1 ? '' : 's'}
              </Badge>
              <Badge variant="outline" className="bg-card">
                {comparison.unchanged_cluster_count} unchanged
              </Badge>
              <Badge variant="outline" className="bg-card">
                +{comparison.new_keyword_count} new keyword
                {comparison.new_keyword_count === 1 ? '' : 's'}
              </Badge>
            </div>
            <div className="mt-1 text-muted-foreground">
              New clusters and keywords are measured against the next older research run in
              history, so you can see what changed between passes.
            </div>
          </div>
        ) : null}

        {run.company_information && (
          <Accordion type="single" collapsible className="border rounded-md">
            <AccordionItem value="company-info" className="border-b-0">
              <AccordionTrigger className="px-3 py-2 text-xs font-medium hover:no-underline">
                Company information
              </AccordionTrigger>
              <AccordionContent className="px-3 pb-3 text-xs whitespace-pre-wrap text-foreground/80">
                {run.company_information}
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        )}

        {run.pages.length > 0 && (
          <div>
            <div className="text-xs font-medium mb-1.5">Pages ({run.pages.length})</div>
            <ul className="space-y-0.5 text-xs">
              {run.pages.map((p) => (
                <li key={p}>
                  <a
                    href={p}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-teal hover:underline inline-flex items-center gap-1"
                  >
                    {p}
                    <ExternalLink className="h-3 w-3" />
                  </a>
                </li>
              ))}
            </ul>
          </div>
        )}

        <Separator />

        <div className="space-y-4">
          {run.clusters.length === 0 ? (
            <div className="rounded border border-dashed p-4 text-center text-sm text-muted-foreground">
              No clusters in this run.
            </div>
          ) : (
            run.clusters.map((cluster, i) => (
              <ClusterCard
                key={`${cluster.cluster_name}-${i}`}
                cluster={cluster}
                comparison={comparison?.cluster_by_name[normalizeKeywordResearchText(cluster.cluster_name)] ?? null}
              />
            ))
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function ClusterCard({
  cluster,
  comparison,
}: {
  cluster: KeywordResearchHistoryClusterDetail;
  comparison: KeywordResearchClusterComparison | null;
}) {
  const selectedCount = cluster.keywords.filter(
    (keyword) => keyword.selected_for_campaign,
  ).length;
  const newKeywordSet = useMemo(
    () =>
      new Set(
        (comparison?.new_keywords ?? []).map((keyword) =>
          normalizeKeywordResearchText(keyword),
        ),
      ),
    [comparison?.new_keywords],
  );
  return (
    <div className="rounded-md border p-3 space-y-2">
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="font-medium text-sm">{cluster.cluster_name}</div>
          {cluster.landing_page && (
            <a
              href={cluster.landing_page}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-teal hover:underline inline-flex items-center gap-1 mt-0.5"
            >
              {cluster.landing_page}
              <ExternalLink className="h-3 w-3" />
            </a>
          )}
        </div>
        <div className="flex flex-wrap justify-end gap-1">
          {comparison?.is_new_cluster ? (
            <Badge variant="outline" className="border-teal/30 bg-teal-light/20 text-teal-dark">
              New cluster
            </Badge>
          ) : comparison?.is_changed_cluster ? (
            <Badge variant="outline" className="border-coral/30 bg-coral-light/20 text-coral-dark">
              {comparison.new_keyword_count > 0
                ? `+${comparison.new_keyword_count} keyword${comparison.new_keyword_count === 1 ? '' : 's'}`
                : `-${comparison.removed_keyword_count} keyword${comparison.removed_keyword_count === 1 ? '' : 's'}`}
            </Badge>
          ) : comparison ? (
            <Badge variant="outline" className="bg-card text-muted-foreground">
              Unchanged
            </Badge>
          ) : null}
          {selectedCount > 0 && (
            <Badge variant="outline" className="bg-teal-light/30 text-teal-dark">
              {selectedCount} selected
            </Badge>
          )}
          <UsedPill cluster={cluster} />
        </div>
      </div>

      {cluster.keywords.length > 0 && (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="h-8 text-xs">Keyword</TableHead>
              <TableHead className="h-8 text-xs text-right w-20">Selected</TableHead>
              <TableHead className="h-8 text-xs text-right w-20">CPC</TableHead>
              <TableHead className="h-8 text-xs text-right w-20">Volume</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {cluster.keywords.map((kw, i) => (
              <TableRow key={`${kw.keyword}-${i}`}>
                <TableCell className="text-xs py-1.5">
                  <div className="flex flex-wrap items-center gap-2">
                    <span>{kw.keyword}</span>
                    {newKeywordSet.has(normalizeKeywordResearchText(kw.keyword)) ? (
                      <Badge
                        variant="outline"
                        className="border-teal/30 bg-teal-light/20 text-[10px] text-teal-dark"
                      >
                        New
                      </Badge>
                    ) : null}
                  </div>
                </TableCell>
                <TableCell className="text-xs py-1.5 text-right">
                  {kw.selected_for_campaign ? (
                    <Badge variant="outline" className="bg-teal-light/20 text-teal-dark">
                      Yes
                    </Badge>
                  ) : (
                    '—'
                  )}
                </TableCell>
                <TableCell className="text-xs py-1.5 text-right tabular-nums">
                  {typeof kw.cpc === 'number' ? `$${kw.cpc.toFixed(2)}` : '—'}
                </TableCell>
                <TableCell className="text-xs py-1.5 text-right tabular-nums">
                  {typeof kw.volume === 'number' ? kw.volume.toLocaleString() : '—'}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
}

function UsedPill({ cluster }: { cluster: KeywordResearchHistoryClusterDetail }) {
  if (cluster.used) {
    return (
      <div className="flex items-center gap-1 text-xs">
        <Badge className="bg-teal-light text-teal-dark border-teal-dark/20 inline-flex items-center gap-1">
          <CheckCircle2 className="h-3 w-3" />
          Used
        </Badge>
        {cluster.used_in_campaign_run_id && (
          <span
            className="text-xs text-muted-foreground inline-flex items-center"
            title={`Campaign run ${cluster.used_in_campaign_run_id}`}
          >
            <ArrowUpRight className="h-3 w-3" />
          </span>
        )}
      </div>
    );
  }
  return (
    <Badge variant="secondary" className="inline-flex items-center gap-1 text-xs">
      <XCircle className="h-3 w-3" />
      Not used
    </Badge>
  );
}

export default KeywordResearchHistoryPanel;
